import {
  AppShell,
  Burger,
  Button,
  Center,
  Container,
  Divider,
  Drawer,
  Group,
  Loader,
  NavLink,
  Stack,
  Text,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { Suspense } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '../auth/AuthContext';
import { ru } from '../i18n/ru';
import { accentText } from '../theme';
import { AppFooter } from './AppFooter';
import { Logo } from './Logo';

const publicNavItems = [
  { to: '/', label: ru.nav.home },
  { to: '/news', label: ru.nav.news },
  { to: '/info', label: ru.nav.info },
];

/** Сайт коллектива: пользователи приходят оттуда, оставляем дорогу обратно. */
const SITE_URL = 'https://favoritstarss.ru';

const authRoutes = ['/login', '/register', '/forgot-password', '/reset-password'];

export function AppLayout() {
  const [opened, { toggle, close }] = useDisclosure(false);
  const { pathname } = useLocation();
  const { user, isAuthenticated, hasRole, logout } = useAuth();
  const navigate = useNavigate();

  const navItems = [
    ...publicNavItems,
    ...(isAuthenticated
      ? [
          { to: '/students', label: ru.students.myTitle },
          { to: '/payments', label: ru.nav.payments },
          { to: '/subscriptions', label: ru.subscriptions.myTitle },
          { to: '/services/orders', label: ru.services.myTitle },
          { to: '/profile', label: ru.nav.profile },
        ]
      : []),
  ];

  const adminNavItems = hasRole('Admin')
    ? [
        { to: '/admin/groups', label: ru.groups.title },
        { to: '/admin/students', label: ru.students.allTitle },
        { to: '/admin/users', label: ru.users.title },
        { to: '/admin/news', label: ru.news.manageTitle },
        { to: '/admin/services', label: ru.services.title },
        { to: '/admin/subscriptions', label: ru.subscriptions.registryTitle },
        { to: '/admin/payments', label: ru.payments.registryTitle },
        { to: '/admin/reports', label: ru.reports.title },
        { to: '/admin/reports/services', label: ru.services.reportTitle },
        { to: '/admin/month', label: ru.monthReport.title },
        { to: '/admin/pages', label: ru.pages.manageTitle },
        { to: '/admin/contract', label: ru.contracts.title },
        { to: '/admin/outbox', label: ru.news.outboxTitle },
      ]
    : [];

  const onAuthPage = authRoutes.includes(pathname);

  const onLogout = async () => {
    close();
    await logout();
    void navigate('/', { replace: true });
  };

  return (
    <AppShell header={{ height: 64 }} padding={{ base: 'md', sm: 'xl' }}>
      <AppShell.Header bg="white" style={{ borderBottom: '1px solid var(--mantine-color-gray-2)' }}>
        <Group h="100%" px={{ base: 'md', sm: 'xl' }} justify="space-between" wrap="nowrap">
          <Group gap="md" wrap="nowrap" style={{ minWidth: 0 }}>
            <Burger opened={opened} onClick={toggle} size="sm" aria-label={ru.nav.menu} />
            <Link to="/" style={{ textDecoration: 'none' }} onClick={close}>
              <Logo />
            </Link>
          </Group>

          {isAuthenticated ? (
            <Text size="sm" c="graphite.6" visibleFrom="sm" truncate maw={220}>
              {user?.fullName}
            </Text>
          ) : (
            // На самих страницах входа и регистрации кнопка дублировала бы форму.
            !onAuthPage && (
              <Button component={Link} to="/login" size="xs" style={{ flexShrink: 0 }}>
                {ru.nav.login}
              </Button>
            )
          )}
        </Group>
      </AppShell.Header>

      {/* Выезжающая панель вместо постоянного меню — одинаково на телефоне
          и на широком экране, как в мобильных приложениях. */}
      <Drawer
        opened={opened}
        onClose={close}
        size={288}
        padding={0}
        withCloseButton={false}
        overlayProps={{ backgroundOpacity: 0.35, blur: 2 }}
        // Тело панели по умолчанию высотой по содержимому, поэтому блок
        // с учётной записью не прижимался бы к низу.
        styles={{
          content: { display: 'flex', flexDirection: 'column' },
          body: { flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 },
        }}
      >
        <Stack gap={0} h="100%">
          <Group justify="space-between" px="md" h={64} wrap="nowrap">
            <Logo />
            <Burger opened onClick={close} size="sm" aria-label={ru.nav.menuClose} />
          </Group>

          <Divider />

          <Stack gap={2} p="xs">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                component={Link}
                to={item.to}
                label={item.label}
                active={pathname === item.to}
                variant="light"
                onClick={close}
              />
            ))}

            {adminNavItems.length > 0 && (
              <>
                <Text
                  size="xs"
                  c="graphite.5"
                  px="sm"
                  pt="md"
                  pb={4}
                  style={{ textTransform: 'uppercase', letterSpacing: '0.14em' }}
                >
                  {ru.nav.administration}
                </Text>

                {adminNavItems.map((item) => (
                  <NavLink
                    key={item.to}
                    component={Link}
                    to={item.to}
                    label={item.label}
                    active={pathname === item.to}
                    variant="light"
                    onClick={close}
                  />
                ))}
              </>
            )}
          </Stack>

          <Divider />

          <Stack gap="sm" p="md" mt="auto">
            {isAuthenticated ? (
              <>
                <Stack gap={2}>
                  <Text size="sm" c="graphite.9" fw={500}>
                    {user?.fullName}
                  </Text>
                  <Text size="xs" c="graphite.5">
                    {user?.email}
                  </Text>
                </Stack>
                <Button variant="default" size="sm" onClick={() => void onLogout()}>
                  {ru.nav.logout}
                </Button>
              </>
            ) : (
              <>
                <Button component={Link} to="/login" size="sm" onClick={close}>
                  {ru.nav.login}
                </Button>
                <Button component={Link} to="/register" size="sm" variant="default" onClick={close}>
                  {ru.nav.register}
                </Button>
              </>
            )}

            <Divider my={4} />

            <Text
              component="a"
              href={SITE_URL}
              size="xs"
              c={accentText}
              fw={500}
              target="_blank"
              rel="noopener noreferrer"
            >
              {ru.nav.siteLink}
            </Text>
          </Stack>
        </Stack>
      </Drawer>

      <AppShell.Main>
        <Container size="md" px={0}>
          {/* Один Suspense на все маршруты, которые грузятся отдельными
              частями: подвал и шапка при этом остаются на месте. */}
          <Suspense
            fallback={
              <Center h={200}>
                <Loader />
              </Center>
            }
          >
            <Outlet />
          </Suspense>
          <AppFooter />
        </Container>
      </AppShell.Main>
    </AppShell>
  );
}
